import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Typography,
  Container,
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  AppBar,
  Toolbar,
  IconButton,
  Stack,
  Chip,
  Paper,
  Avatar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  MonitorHeart,
  TrendingUp,
  Security,
  Cloud,
  Group,
  Analytics,
  Login,
  PersonAdd,
  Dashboard,
  CheckCircle,
  Timeline,
  Insights,
  HealthAndSafety,
  Favorite,
  Assessment,
  Share,
  MobileScreenShare,
  Notifications
} from '@mui/icons-material';

const Home = () => {
  const navigate = useNavigate();
  const { token } = useSelector((state) => state.auth);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleGetStarted = () => {
    if (token) {
      navigate('/dashboard');
    } else {
      navigate('/register');
    }
  };

  const handleSignIn = () => {
    navigate('/login');
  };

  const features = [
    {
      icon: <MonitorHeart />,
      title: "Hypertension Monitoring",
      description: "Record and monitor your blood pressure readings with intelligent analysis to detect and manage hypertension patterns."
    },
    {
      icon: <TrendingUp />,
      title: "Hypertension Trend Analysis",
      description: "Advanced analytics to identify hypertension trends and patterns in your blood pressure over time."
    },
    {
      icon: <Notifications />,
      title: "Hypertension Alerts",
      description: "Get notified when readings indicate hypertension risk or when it's time for your next measurement."
    },
    {
      icon: <Assessment />,
      title: "Comprehensive Hypertension Reports",
      description: "Generate detailed reports for healthcare providers with charts and hypertension risk assessment."
    },
    {
      icon: <MobileScreenShare />,
      title: "Device Integration",
      description: "Connect with popular blood pressure monitors for automatic data sync and continuous hypertension monitoring."
    },
    {
      icon: <Group />,
      title: "Healthcare Sharing",
      description: "Safely share your hypertension data with healthcare providers and family members."
    }
  ];

  const benefits = [
    "Track blood pressure trends to identify hypertension patterns",
    "Identify hypertension triggers and lifestyle factors",
    "Share hypertension data with healthcare providers",
    "Get personalized insights and hypertension management recommendations",
    "Set reminders for regular hypertension monitoring",
    "Export hypertension data for medical appointments and analysis"
  ];

  return (
    <Box sx={{ flexGrow: 1, minHeight: '100vh' }}>
      {/* Header */}
      <AppBar position="static" sx={{ backgroundColor: 'primary.main', boxShadow: 1 }}>
        <Toolbar>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <Favorite sx={{ mr: 1, color: 'secondary.main' }} />
            <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
              CheckYourMate
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {token ? (
              <Button
                color="inherit"
                startIcon={<Dashboard />}
                onClick={() => navigate('/dashboard')}
                sx={{ textTransform: 'none' }}
              >
                Dashboard
              </Button>
            ) : (
              <>
                <Button
                  color="inherit"
                  onClick={() => navigate('/about')}
                  sx={{ textTransform: 'none' }}
                >
                  About
                </Button>
                <Button
                  color="inherit"
                  startIcon={<Login />}
                  onClick={handleSignIn}
                  sx={{ textTransform: 'none' }}
                >
                  Sign In
                </Button>
                <Button
                  color="secondary"
                  variant="contained"
                  startIcon={<PersonAdd />}
                  onClick={handleGetStarted}
                  sx={{ textTransform: 'none' }}
                >
                  Get Started
                </Button>
              </>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          py: { xs: 8, md: 12 },
          textAlign: 'center'
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant={isMobile ? "h3" : "h2"}
            component="h1"
            gutterBottom
            sx={{ fontWeight: 'bold', mb: 3 }}
          >
            Take Control of Your
            <Box component="span" sx={{ color: 'secondary.main', ml: 1 }}>
              Blood Pressure & Hypertension
            </Box>
          </Typography>
          <Typography
            variant="h6"
            sx={{ mb: 4, opacity: 0.9, maxWidth: '600px', mx: 'auto' }}
          >
            Monitor, analyze, and manage your blood pressure to prevent and control hypertension with our comprehensive health tracking platform.
            Get insights that help you maintain optimal cardiovascular health and reduce hypertension risk.
          </Typography>
          <Stack
            direction={isMobile ? "column" : "row"}
            spacing={2}
            justifyContent="center"
            sx={{ mb: 4 }}
          >
            <Button
              variant="contained"
              size="large"
              color="secondary"
              startIcon={<MonitorHeart />}
              onClick={handleGetStarted}
              sx={{ 
                px: 4, 
                py: 1.5, 
                textTransform: 'none',
                fontSize: '1.1rem',
                fontWeight: 'bold'
              }}
            >
              {token ? 'Go to Dashboard' : 'Start Monitoring'}
            </Button>
            <Button
              variant="outlined"
              size="large"
              sx={{ 
                px: 4, 
                py: 1.5, 
                textTransform: 'none',
                fontSize: '1.1rem',
                color: 'white',
                borderColor: 'white',
                '&:hover': {
                  borderColor: 'secondary.main',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)'
                }
              }}
              startIcon={<Insights />}
              onClick={() => navigate('/about')}
            >
              Learn More
            </Button>
          </Stack>
          <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 1 }}>
            {['Free to Use', 'Secure & Private', 'Healthcare Ready'].map((tag) => (
              <Chip
                key={tag}
                label={tag}
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  fontWeight: 'bold'
                }}
              />
            ))}
          </Box>
        </Container>
      </Box>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography
          variant="h3"
          component="h2"
          textAlign="center"
          gutterBottom
          sx={{ mb: 6, fontWeight: 'bold' }}
        >
          Why Choose CheckYourMate?
        </Typography>
        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Card 
                sx={{ 
                  height: '100%', 
                  textAlign: 'center',
                  transition: 'transform 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: 4
                  }
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Avatar
                    sx={{
                      backgroundColor: 'primary.main',
                      width: 64,
                      height: 64,
                      mx: 'auto',
                      mb: 2
                    }}
                  >
                    {feature.icon}
                  </Avatar>
                  <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 'bold' }}>
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Benefits Section */}
      <Box sx={{ backgroundColor: 'grey.50', py: 8 }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
                Everything You Need for
                <Box component="span" sx={{ color: 'primary.main', ml: 1 }}>
                  Hypertension Management
                </Box>
              </Typography>
              <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary', fontSize: '1.1rem' }}>
                Our platform provides comprehensive tools to help you monitor, understand, and manage hypertension while improving your overall cardiovascular health.
              </Typography>
              <List>
                {benefits.map((benefit, index) => (
                  <ListItem key={index} sx={{ py: 0.5 }}>
                    <ListItemIcon>
                      <CheckCircle color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary={benefit}
                      primaryTypographyProps={{ fontSize: '1rem' }}
                    />
                  </ListItem>
                ))}
              </List>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper 
                elevation={3} 
                sx={{ 
                  p: 4, 
                  textAlign: 'center',
                  background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
                }}
              >
                <HealthAndSafety sx={{ fontSize: 80, color: 'primary.main', mb: 2 }} />
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                  Your Health, Your Data
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Secure, private, and always under your control. Share with healthcare providers when you choose.
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={handleGetStarted}
                  sx={{ textTransform: 'none', fontWeight: 'bold' }}
                >
                  {token ? 'View Dashboard' : 'Get Started Today'}
                </Button>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Footer */}
      <Box sx={{ backgroundColor: 'grey.900', color: 'white', py: 4 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid item xs={12} md={8}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Favorite sx={{ mr: 1, color: 'secondary.main' }} />
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  CheckYourMate
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                Your comprehensive blood pressure monitoring and hypertension management platform. Take control of your cardiovascular health with intelligent tracking, analysis, and hypertension prevention tools.
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                Quick Links
              </Typography>
              <Stack spacing={1}>
                <Button
                  color="inherit"
                  size="small"
                  sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                  onClick={() => navigate('/about')}
                >
                  About
                </Button>
                <Button
                  color="inherit"
                  size="small"
                  sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                  onClick={() => navigate('/register')}
                >
                  Sign Up
                </Button>
                <Button
                  color="inherit"
                  size="small"
                  sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                  onClick={() => navigate('/login')}
                >
                  Sign In
                </Button>
              </Stack>
            </Grid>
          </Grid>
          <Divider sx={{ my: 3, borderColor: 'grey.700' }} />
          <Typography variant="body2" textAlign="center" sx={{ opacity: 0.6 }}>
            © 2024 CheckYourMate. All rights reserved.
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};

export default Home; 